/**
 * Auth service - provides access to DPOMAuth instance for token retrieval.
 * Used by request interceptor to get fresh tokens (with auto-refresh) instead of
 * reading stale tokens from localStorage.
 * @author {Cognizant Technology Solutions}
 */
let authInstance = null;

/**
 * Stores the DPOMAuth instance used for token retrieval.
 * @param {Object} instance The DPOMAuth instance
 * @returns {void}
 */
export function setAuthInstance(instance) {
  authInstance = instance;
}

/**
 * Returns the stored DPOMAuth instance.
 * @returns {?Object} The DPOMAuth instance, or null if not set
 */
export function getAuthInstance() {
  return authInstance;
}

/**
 * Get a valid access token, refreshing if necessary.
 * Returns null if no auth instance or token unavailable.
 * @returns {Promise<?string>} The access token, or null on failure
 */
export async function getAccessToken() {
  if (!authInstance || typeof authInstance.getAccessToken !== 'function') {
    return null;
  }
  try {
    return await authInstance.getAccessToken();
  } catch (error) {
    console.log('authService getAccessToken error:', error);
    return null;
  }
}

/**
 * Renew access token using refresh token.
 * Use when token expired (e.g. on 401) - handles cases where auto-renew timer didn't fire.
 * @returns {Promise<object|null>} New access token or null on failure
 */
export async function renewAccessToken() {
  if (!authInstance || typeof authInstance.renewAccessToken !== 'function') {
    return null;
  }
  try {
    return await authInstance.renewAccessToken();
  } catch (error) {
    console.log('authService renewAccessToken error:', error);
    return null;
  }
}

/**
 * Get id token for user claims (email, legacy_username).
 * Returns null if no auth instance or token unavailable.
 * @returns {Promise<?Object>} The id token, or null on failure
 */
export async function getIdToken() {
  if (!authInstance || typeof authInstance.getIdToken !== 'function') {
    return null;
  }
  try {
    return await authInstance.getIdToken();
  } catch (error) {
    console.log('authService getIdToken error:', error);
    return null;
  }
}
